import { type ReactElement, type ReactNode, useEffect, useRef, useState } from 'react';

import { ScaledDevice } from '@/components/PreviewLayout';
import { Button } from '@/components/ui/button';
import { createActionPreview } from '@/lib/api';
import { systemClock } from '@/lib/clock';

/** A fresh token this long before the old one runs out, so "Vorschau neu laden" never meets it expired. */
const RENEW_BEFORE_MS = 60_000;

type PreviewState =
  | { readonly status: 'loading' }
  | { readonly status: 'error' }
  | { readonly status: 'ready'; readonly url: string };

interface PreviewProps {
  readonly actionId: string;
  readonly fieldPosition?: number;
  readonly title: string;
  /** Starts on the phone, where most visitors are; the door page and the example do. */
  readonly defaultMobile?: boolean;
  /**
   * Raised after a write that changes the page: the frame loads again with the newest token and
   * stays on the device it shows. A new key would put it back on the default device.
   */
  readonly revision?: number;
  /** Added to the preview's address, e.g. a frame and its look (D39). Changes load it again. */
  readonly query?: string;
  /** The desktop device's width; a frame in an article is narrower than a screen (D39). */
  readonly desktopWidth?: number;
}

/**
 * The real participant page of one action in a frame: the calendar, or one door's page.
 *
 * A frame rather than a rebuilt calendar, because only the participant page itself can promise
 * that the preview is what a visitor gets (ADR 0001). The token is minted here and never shown:
 * it opens every door of the action, unreleased contributions included, so it stays out of the
 * console and out of anything that could be copied into a ticket.
 *
 * In the running action the preview carries the test run (D21): answer, completion, the mail
 * to the typed address. So the sandbox allows forms, and the token lives an hour while a
 * fresh one is minted a minute before. The fresh one does not go into the frame on its own:
 * a new address would reload it and throw away a half-filled form. It waits for "Vorschau neu
 * laden", which is also what the page inside asks for once its token has run out.
 *
 * The sandbox is a second line after `frame-ancestors`: the page may run its scripts in its own
 * origin and post its forms, but it cannot open windows or navigate the backoffice.
 *
 * Keyed by its target, so another door or action starts with a token of its own.
 */
export function ParticipantPreview(props: PreviewProps): ReactElement {
  return <PreviewFrame key={`${props.actionId}/${String(props.fieldPosition)}`} {...props} />;
}

function PreviewFrame({
  actionId,
  fieldPosition,
  title,
  defaultMobile = false,
  revision = 0,
  query,
  desktopWidth,
}: PreviewProps): ReactElement {
  const [state, setState] = useState<PreviewState>({ status: 'loading' });
  const [reloads, setReloads] = useState(0);
  /** The newest address, renewed in the background; the frame takes it on "neu laden". */
  const latest = useRef<string | null>(null);

  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const mint = (): void => {
      createActionPreview(actionId, fieldPosition)
        .then((outcome) => {
          if (!active) {
            return;
          }
          if (!outcome.ok) {
            // A failed renewal leaves a working frame alone; only a first failure is shown.
            if (latest.current === null) {
              setState({ status: 'error' });
            }
            return;
          }
          if (latest.current === null) {
            setState({ status: 'ready', url: outcome.previewUrl });
          }
          latest.current = outcome.previewUrl;
          const left = Date.parse(outcome.expiresAt) - systemClock.now().getTime();
          // At least a minute: a browser clock far ahead of the server must not become a loop.
          timer = setTimeout(mint, Math.max(left - RENEW_BEFORE_MS, RENEW_BEFORE_MS));
        })
        .catch((error: unknown) => {
          console.error('[ParticipantPreview] preview failed', error);
          if (active && latest.current === null) {
            setState({ status: 'error' });
          }
        });
    };

    mint();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [actionId, fieldPosition]);

  useEffect(() => {
    if (latest.current !== null) {
      setState({ status: 'ready', url: latest.current });
    }
  }, [revision]);

  return (
    <FramedPage
      src={state.status === 'ready' ? withQuery(state.url, query) : null}
      title={title}
      defaultMobile={defaultMobile}
      desktopWidth={desktopWidth}
      revision={reloads + revision}
      waiting={
        state.status === 'loading'
          ? 'Vorschau wird geladen …'
          : 'Die Vorschau konnte nicht geladen werden.'
      }
      actions={
        <Button
          size="sm"
          variant="outline"
          disabled={state.status !== 'ready'}
          onClick={() => {
            if (latest.current !== null) {
              setState({ status: 'ready', url: latest.current });
              setReloads((count) => count + 1);
            }
          }}
        >
          Vorschau neu laden
        </Button>
      }
    />
  );
}

/** The preview's address with `query` after the token. */
function withQuery(url: string, query: string | undefined): string {
  return query === undefined ? url : `${url}${url.includes('?') ? '&' : '?'}${query}`;
}

/** The two devices a page is shown on, at the size a visitor's screen has. */
const DESKTOP = { width: 1280, height: 900 } as const;
const MOBILE = { width: 390, height: 844 } as const;

interface FramedPageProps {
  /** The page; `null` while there is none yet, then `waiting` stands in its place. */
  readonly src: string | null;
  readonly title: string;
  readonly defaultMobile?: boolean;
  /** Raised to load the same address again. */
  readonly revision?: number;
  readonly waiting?: string;
  /** Further buttons next to the device switch. */
  readonly actions?: ReactNode;
  readonly desktopWidth?: number;
}

/**
 * A participant page in a fixed device frame, shrunk to the space it gets. A frame as wide as
 * its column would show the desktop page with the phone layout; this one keeps the device's
 * width, so the page picks the layout a visitor sees.
 */
export function FramedPage({
  src,
  title,
  defaultMobile = false,
  revision = 0,
  waiting = 'Vorschau wird geladen …',
  actions,
  desktopWidth = DESKTOP.width,
}: FramedPageProps): ReactElement {
  const [mobile, setMobile] = useState(defaultMobile);
  const device = mobile ? MOBILE : { ...DESKTOP, width: desktopWidth };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant={mobile ? 'outline' : 'default'}
          aria-pressed={!mobile}
          onClick={() => {
            setMobile(false);
          }}
        >
          Desktop
        </Button>
        <Button
          size="sm"
          variant={mobile ? 'default' : 'outline'}
          aria-pressed={mobile}
          onClick={() => {
            setMobile(true);
          }}
        >
          Mobil
        </Button>
        {actions}
      </div>

      {src !== null ? (
        <ScaledDevice width={device.width}>
          <iframe
            key={revision}
            src={src}
            title={title}
            sandbox="allow-scripts allow-same-origin allow-forms"
            className="block border border-border"
            style={{ width: device.width, height: device.height }}
          />
        </ScaledDevice>
      ) : (
        <p className="text-sm text-muted-foreground">{waiting}</p>
      )}
    </div>
  );
}
